import createClient, { type Middleware } from 'openapi-fetch'
import type { paths } from './schema'
import { getToken } from '@/auth/session'
import { ApiError } from './errors'

/**
 * Single typed API client for the whole app. Every path/param/body/response is
 * checked against the generated contract (`schema.d.ts`) at compile time.
 */
export const api = createClient<paths>({ baseUrl: '' })

// Inject the Bearer session token on every request.
const authMiddleware: Middleware = {
  onRequest({ request }) {
    const token = getToken()
    if (token) request.headers.set('Authorization', `Bearer ${token}`)
    return request
  },
}
api.use(authMiddleware)

/** A fresh Idempotency-Key for a safely-retryable mutation. */
export function idempotencyKey(): string {
  return crypto.randomUUID()
}

interface FetchResult<T> {
  data?: T
  error?: unknown
  response: Response
}

/**
 * Unwrap an openapi-fetch result for use with TanStack Query: returns `data` on
 * success, throws a normalized {@link ApiError} on any HTTP/network failure.
 */
export async function unwrap<T>(promise: Promise<FetchResult<T>>): Promise<T> {
  let result: FetchResult<T>
  try {
    result = await promise
  } catch (cause) {
    throw ApiError.network(cause)
  }
  const { data, error, response } = result
  if (!response.ok || error !== undefined) {
    throw ApiError.from(error, response)
  }
  return data as T
}
